import { useEffect, useMemo, useRef, useState } from "react";
import { productFromApi, type ApiProduct, type CartResponse } from "../lib/catalog";
import type { CartLine, CustomerProfile, Product } from "../types/catalog";

const emptyCustomerProfile: CustomerProfile = {
  customerEmail: "",
  customerName: "",
  phone: "",
};

function normalizeCartResponse(value: unknown): CartResponse {
  if (typeof value !== "object" || value === null) {
    return {
      customerProfile: emptyCustomerProfile,
      items: [],
    };
  }

  const candidate = value as Partial<CartResponse> & {
    items?: Array<{ product?: ApiProduct; quantity?: number }>;
  };

  const customerProfile = candidate.customerProfile ?? emptyCustomerProfile;

  return {
    customerProfile: {
      customerEmail: typeof customerProfile.customerEmail === "string" ? customerProfile.customerEmail : "",
      customerName: typeof customerProfile.customerName === "string" ? customerProfile.customerName : "",
      phone: typeof customerProfile.phone === "string" ? customerProfile.phone : "",
    },
    items: Array.isArray(candidate.items)
      ? candidate.items
          .flatMap((item) => {
            if (
              typeof item !== "object" ||
              item === null ||
              typeof item.quantity !== "number" ||
              typeof item.product !== "object" ||
              item.product === null
            ) {
              return [];
            }

            return [
              {
                product: productFromApi(item.product),
                quantity: item.quantity,
              },
            ];
          })
      : [],
  };
}

export function useCart() {
  const [cartItems, setCartItems] = useState<CartLine[]>([]);
  const [cartStatus, setCartStatus] = useState("Loading cart...");
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile>(emptyCustomerProfile);
  const requestVersionRef = useRef(0);

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartTotalKes = useMemo(
    () => cartItems.reduce((total, item) => total + item.product.priceKes * item.quantity, 0),
    [cartItems]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadCart() {
      try {
        const response = await fetch("/api/cart");

        if (!response.ok) {
          throw new Error("Cart could not be loaded.");
        }

        const body: unknown = await response.json();
        const nextCart = normalizeCartResponse(body);

        if (!isMounted) {
          return;
        }

        setCartItems(nextCart.items);
        setCustomerProfile(nextCart.customerProfile);
        setCartStatus("");
      } catch {
        if (isMounted) {
          setCartStatus("Cart could not be loaded.");
        }
      }
    }

    void loadCart();

    return () => {
      isMounted = false;
    };
  }, []);

  async function persistCart(nextItems: CartLine[]) {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setCartStatus("Saving cart...");

    const response = await fetch("/api/cart", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: nextItems.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
      }),
    });

    if (!response.ok) {
      setCartStatus("Cart could not be updated.");
      return false;
    }

    const body: unknown = await response.json();
    const nextCart = normalizeCartResponse(body);

    if (requestVersion !== requestVersionRef.current) {
      return true;
    }

    setCartItems(nextCart.items);
    setCustomerProfile(nextCart.customerProfile);
    setCartStatus("");
    return true;
  }

  function handleAddToCart(product: Product) {
    const nextItems = ((currentItems: CartLine[]) => {
      const existingItem = currentItems.find((item) => item.product.id === product.id);

      if (existingItem) {
        return currentItems.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: Math.min(item.quantity + 1, product.stockQty) }
            : item
        );
      }

      return [{ product, quantity: 1 }, ...currentItems];
    })(cartItems);

    setCartItems(nextItems);
    void persistCart(nextItems);
  }

  function handleUpdateCartQuantity(productId: string, quantity: number) {
    const nextItems = cartItems
      .map((item) =>
        item.product.id === productId
          ? {
              ...item,
              quantity: Math.max(1, Math.min(quantity, item.product.stockQty)),
            }
          : item
      )
      .filter((item) => item.quantity > 0);

    setCartItems(nextItems);
    void persistCart(nextItems);
  }

  function handleRemoveFromCart(productId: string) {
    const nextItems = cartItems.filter((item) => item.product.id !== productId);
    setCartItems(nextItems);
    void persistCart(nextItems);
  }

  function clearCart() {
    setCartItems([]);
    setCartStatus("");
  }

  function rememberCustomerProfile(profile: Partial<CustomerProfile>) {
    setCustomerProfile((currentProfile) => ({
      customerEmail: profile.customerEmail ?? currentProfile.customerEmail,
      customerName: profile.customerName ?? currentProfile.customerName,
      phone: profile.phone ?? currentProfile.phone,
    }));
  }

  return {
    cartCount,
    cartItems,
    cartStatus,
    cartTotalKes,
    clearCart,
    customerProfile,
    handleAddToCart,
    handleRemoveFromCart,
    handleUpdateCartQuantity,
    rememberCustomerProfile,
  };
}
